<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Dynamic Sidebar -->
    <component 
      :is="layoutMap[currentModule].sidebar" 
      v-model:isOpen="isSidebarOpen" 
    />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop, fixing overlap -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <component 
          :is="layoutMap[currentModule].topbar"
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div class="max-w-5xl mx-auto">
          
          <!-- Form Card -->
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8 mb-8">
            
            <!-- Header -->
            <div class="mb-8">
              <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Employee Onboarding</h1>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Please complete all sections to finalize the onboarding process.</p>
            </div>

            <!-- Draft Indicator -->
            <div v-if="isLoadingDraft" class="mb-6 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
              <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              Checking for a previously saved draft...
            </div>
            <div v-else-if="employeeId" class="mb-6 flex items-center gap-2 text-sm text-[#035a76] dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Continuing a saved draft. Your previously saved data has been loaded.
            </div>

            <!-- Modern Progress Indicator -->
            <div class="mb-10">
              <div class="flex items-center justify-between mb-4">
                <div v-for="(step, index) in steps" :key="index" class="flex flex-col items-center flex-1 relative">
                  <div 
                    :class="[
                      'w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 z-10 border-2',
                      currentStep >= index + 1 
                        ? 'bg-[#76ba51] border-[#76ba51] text-white shadow-md shadow-[#76ba51]/20' 
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                    ]"
                  >
                    <span v-if="currentStep > index + 1">✓</span>
                    <span v-else>{{ index + 1 }}</span>
                  </div>
                  <span 
                    :class="[
                      'mt-2 text-xs font-semibold text-center hidden sm:block transition-colors',
                      currentStep >= index + 1 ? 'text-[#76ba51] dark:text-[#76ba51]' : 'text-gray-500 dark:text-gray-400'
                    ]"
                  >
                    {{ step }}
                  </span>
                  <!-- Connector Line -->
                  <div 
                    v-if="index < steps.length - 1"
                    :class="[
                      'absolute top-4 left-1/2 w-full h-0.5 -z-0 transition-colors duration-300',
                      currentStep > index + 1 ? 'bg-[#76ba51]' : 'bg-gray-200 dark:bg-gray-700'
                    ]"
                  ></div>
                </div>
              </div>
            </div>

            <form novalidate @submit.prevent="submitForm" class="space-y-8">
              
              <!-- Step 1: Personal Information -->
              <div v-show="currentStep === 1" class="space-y-6 animate-fade-in">
                <h2 class="text-xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-3 flex items-center gap-2">
                  <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  Personal Information
                </h2>
                
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name <span class="text-red-500">*</span></label>
                    <input v-model="form.personal.firstName" type="text" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.firstName}">
                    <p v-if="errors.firstName" class="error-msg">{{ errors.firstName }}</p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Middle Name</label>
                    <input v-model="form.personal.middleName" type="text" class="input-base">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name <span class="text-red-500">*</span></label>
                    <input v-model="form.personal.lastName" type="text" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.lastName}">
                    <p v-if="errors.lastName" class="error-msg">{{ errors.lastName }}</p>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gender <span class="text-red-500">*</span></label>
                    <select v-model="form.personal.gender" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.gender}">
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                    <p v-if="errors.gender" class="error-msg">{{ errors.gender }}</p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date of Birth <span class="text-red-500">*</span></label>
                    <input v-model="form.personal.dob" type="date" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.dob}">
                    <p v-if="errors.dob" class="error-msg">{{ errors.dob }}</p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Place of Birth <span class="text-red-500">*</span></label>
                    <input v-model="form.personal.placeOfBirth" type="text" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.placeOfBirth}">
                    <p v-if="errors.placeOfBirth" class="error-msg">{{ errors.placeOfBirth }}</p>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nationality <span class="text-red-500">*</span></label>
                    <input v-model="form.personal.nationality" type="text" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.nationality}">
                    <p v-if="errors.nationality" class="error-msg">{{ errors.nationality }}</p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number <span class="text-red-500">*</span></label>
                    <input v-model="form.personal.phone" type="tel" inputmode="numeric" maxlength="10" @input="form.personal.phone = form.personal.phone.replace(/\D/g, '')" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.phone}">
                    <p v-if="errors.phone" class="error-msg">{{ errors.phone }}</p>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address <span class="text-red-500">*</span></label>
                  <input v-model="form.personal.email" type="email" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.email}">
                  <p v-if="errors.email" class="error-msg">{{ errors.email }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Residential Address <span class="text-red-500">*</span></label>
                  <textarea v-model="form.personal.address" rows="3" class="input-base resize-none" :class="{'border-red-500 focus:ring-red-500': errors.address}"></textarea>
                  <p v-if="errors.address" class="error-msg">{{ errors.address }}</p>
                </div>
              </div>

              <!-- Step 2: Marital Status & Family -->
              <div v-show="currentStep === 2" class="space-y-6 animate-fade-in">
                <h2 class="text-xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-3 flex items-center gap-2">
                  <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  Marital Status & Family Information
                </h2>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Marital Status <span class="text-red-500">*</span></label>
                  <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div v-for="status in maritalStatuses" :key="status.value" class="relative">
                      <input :id="`marital-${status.value}`" v-model="form.marital.status" :value="status.value" type="radio" class="peer sr-only">
                      <label :for="`marital-${status.value}`" class="block text-center p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer peer-checked:border-[#035a76] peer-checked:bg-[#035a76]/5 peer-checked:text-[#035a76] dark:peer-checked:text-blue-400 transition-all hover:bg-gray-50 dark:hover:bg-gray-600">
                        {{ status.label }}
                      </label>
                    </div>
                  </div>
                  <p v-if="errors.status" class="error-msg mt-2">{{ errors.status }}</p>
                </div>

                <div v-if="form.marital.status === 'married'" class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Spouse Name <span class="text-red-500">*</span></label>
                  <input v-model="form.marital.spouseName" type="text" class="input-base bg-white dark:bg-gray-800" :class="{'border-red-500 focus:ring-red-500': errors.spouseName}">
                  <p v-if="errors.spouseName" class="error-msg">{{ errors.spouseName }}</p>
                </div>

                <div class="mt-6 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div class="flex items-center mb-4">
                    <input id="has-children" v-model="form.marital.hasChildren" type="checkbox" class="w-4 h-4 rounded border-gray-300 text-[#035a76] focus:ring-[#035a76]">
                    <label for="has-children" class="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Do you have any children?</label>
                  </div>

                  <div v-if="form.marital.hasChildren" class="space-y-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number of Children <span class="text-red-500">*</span></label>
                      <input v-model.number="form.marital.numberOfChildren" type="number" min="1" max="20" @change="updateChildrenFields" class="input-base w-32">
                    </div>

                    <transition-group name="fade" tag="div" class="space-y-4">
                      <div v-for="(child, index) in form.marital.children" :key="index" class="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm">
                        <div class="flex justify-between items-center mb-3">
                          <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Child {{ index + 1 }}</h3>
                          <button v-if="form.marital.children.length > 1" type="button" @click="removeChild(index)" class="text-red-600 hover:text-red-800 dark:hover:text-red-400 text-sm font-medium flex items-center gap-1">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Remove
                          </button>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Full Name <span class="text-red-500">*</span></label>
                            <input v-model="child.fullName" type="text" class="input-base">
                          </div>
                          <div>
                            <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Age <span class="text-red-500">*</span></label>
                            <input v-model.number="child.age" type="number" min="0" max="30" class="input-base">
                          </div>
                        </div>
                      </div>
                    </transition-group>
                    
                    <button type="button" @click="addChild" class="inline-flex items-center px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-[#035a76] dark:text-blue-400 bg-transparent hover:bg-[#035a76]/5 dark:hover:bg-blue-900/20 transition-colors">
                      <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                      Add Another Child
                    </button>
                  </div>
                </div>

                <div class="mt-6">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">Parents Information</h3>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Father's Name / Emergency Person <span class="text-red-500">*</span></label>
                      <input v-model="form.parents.fatherName" type="text" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.fatherName}">
                      <p v-if="errors.fatherName" class="error-msg">{{ errors.fatherName }}</p>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Father's Contact <span class="text-red-500">*</span></label>
                      <input v-model="form.parents.fatherContact" type="tel" inputmode="numeric" maxlength="10" @input="form.parents.fatherContact = form.parents.fatherContact.replace(/\D/g, '')" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.fatherContact}">
                      <p v-if="errors.fatherContact" class="error-msg">{{ errors.fatherContact }}</p>
                    </div>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mother's Name / Emergency Person <span class="text-red-500">*</span></label>
                      <input v-model="form.parents.motherName" type="text" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.motherName}">
                      <p v-if="errors.motherName" class="error-msg">{{ errors.motherName }}</p>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mother's Contact</label>
                      <input v-model="form.parents.motherContact" type="tel" inputmode="numeric" maxlength="10" @input="form.parents.motherContact = form.parents.motherContact.replace(/\D/g, '')" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.motherContact}">
                      <p v-if="errors.motherContact" class="error-msg">{{ errors.motherContact }}</p>
                    </div>
                  </div>
                  <div class="mt-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Parents' Address <span class="text-red-500">*</span></label>
                    <textarea v-model="form.parents.address" rows="2" class="input-base resize-none" :class="{'border-red-500 focus:ring-red-500': errors.parentsAddress}"></textarea>
                    <p v-if="errors.parentsAddress" class="error-msg">{{ errors.parentsAddress }}</p>
                  </div>
                </div>
              </div>

              <!-- Step 3: Work Experience -->
              <div v-show="currentStep === 3" class="space-y-6 animate-fade-in">
                <h2 class="text-xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-3 flex items-center gap-2">
                  <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  Work Experience
                </h2>
                
                <div v-for="(exp, index) in form.workExperience" :key="index" class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Previous Work {{ index + 1 }}</h3>
                    <button v-if="index > 0" type="button" @click="removeWorkExperience(index)" class="text-red-600 hover:text-red-800 dark:hover:text-red-400 text-sm font-medium flex items-center gap-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      Remove
                    </button>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name <span class="text-red-500">*</span></label>
                      <input v-model="exp.company" type="text" class="input-base bg-white dark:bg-gray-800">
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Position Held <span class="text-red-500">*</span></label>
                      <input v-model="exp.position" type="text" class="input-base bg-white dark:bg-gray-800">
                    </div>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Address <span class="text-red-500">*</span></label>
                      <input v-model="exp.address" type="text" class="input-base bg-white dark:bg-gray-800">
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Person / Number</label>
                      <input v-model="exp.contact" type="text" class="input-base bg-white dark:bg-gray-800">
                    </div>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date <span class="text-red-500">*</span></label>
                      <input v-model="exp.startDate" type="date" class="input-base bg-white dark:bg-gray-800">
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date <span class="text-red-500">*</span></label>
                      <input v-model="exp.endDate" type="date" class="input-base bg-white dark:bg-gray-800">
                    </div>
                  </div>
                  
                  <div class="mt-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason for Leaving <span class="text-red-500">*</span></label>
                    <textarea v-model="exp.reasonForLeaving" rows="2" class="input-base bg-white dark:bg-gray-800 resize-none"></textarea>
                  </div>
                </div>
                
                <button type="button" @click="addWorkExperience" class="inline-flex items-center px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-[#035a76] dark:text-blue-400 bg-transparent hover:bg-[#035a76]/5 dark:hover:bg-blue-900/20 transition-colors">
                  <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                  Add Another Work Experience
                </button>
              </div>

              <!-- Step 4: Education -->
              <div v-show="currentStep === 4" class="space-y-6 animate-fade-in">
                <h2 class="text-xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-3 flex items-center gap-2">
                  <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 14l9-5-9-5-9 5 9 5z" /><path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" /></svg>
                  Education Background
                </h2>
                
                <div v-for="(edu, index) in form.education" :key="index" class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Education {{ index + 1 }}</h3>
                    <button v-if="index > 0" type="button" @click="removeEducation(index)" class="text-red-600 hover:text-red-800 dark:hover:text-red-400 text-sm font-medium flex items-center gap-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      Remove
                    </button>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Certificate / Degree <span class="text-red-500">*</span></label>
                      <select v-model="edu.certificate" class="input-base bg-white dark:bg-gray-800">
                        <option value="">Select Certificate</option>
                        <option value="WASSCE">WASSCE</option>
                        <option value="Diploma">Diploma</option>
                        <option value="Bachelor">Bachelor's Degree</option>
                        <option value="Master">Master's Degree</option>
                        <option value="PhD">PhD</option>
                        <option value="Other">Other</option>
                      </select>
                      <input v-if="edu.certificate === 'Other'" v-model="edu.otherCertificate" type="text" placeholder="Please specify" class="input-base bg-white dark:bg-gray-800 mt-3">
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">School Name <span class="text-red-500">*</span></label>
                      <input v-model="edu.school" type="text" class="input-base bg-white dark:bg-gray-800">
                    </div>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Year <span class="text-red-500">*</span></label>
                      <input v-model="edu.startYear" type="number" min="1900" :max="new Date().getFullYear()" class="input-base bg-white dark:bg-gray-800">
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Year <span class="text-red-500">*</span></label>
                      <input v-model="edu.endYear" type="number" min="1900" :max="new Date().getFullYear()" class="input-base bg-white dark:bg-gray-800">
                    </div>
                  </div>
                </div>
                
                <button type="button" @click="addEducation" class="inline-flex items-center px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-[#035a76] dark:text-blue-400 bg-transparent hover:bg-[#035a76]/5 dark:hover:bg-blue-900/20 transition-colors">
                  <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                  Add Another Education
                </button>
              </div>

              <!-- Step 5: Emergency Contact & Documents -->
              <div v-show="currentStep === 5" class="space-y-8 animate-fade-in">
                <h2 class="text-xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-3 flex items-center gap-2">
                  <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  Emergency Contact
                </h2>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name <span class="text-red-500">*</span></label>
                    <input v-model="form.emergencyContact.fullName" type="text" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.emergencyFullName}">
                    <p v-if="errors.emergencyFullName" class="error-msg">{{ errors.emergencyFullName }}</p>     
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Relationship <span class="text-red-500">*</span></label>
                    <input v-model="form.emergencyContact.relationship" type="text" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.emergencyRelationship}">
                    <p v-if="errors.emergencyRelationship" class="error-msg">{{ errors.emergencyRelationship }}</p>     
                  </div>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number <span class="text-red-500">*</span></label>
                    <input v-model="form.emergencyContact.phone" type="tel" class="input-base" :class="{'border-red-500 focus:ring-red-500': errors.emergencyPhone}">
                    <p v-if="errors.emergencyPhone" class="error-msg">{{ errors.emergencyPhone }}</p> 
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Alternative Phone</label>
                    <input v-model="form.emergencyContact.altPhone" type="tel" class="input-base">
                  </div>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address <span class="text-red-500">*</span></label>
                  <textarea v-model="form.emergencyContact.address" rows="2" class="input-base resize-none"></textarea>
                </div>

                <h2 class="text-xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-3 flex items-center gap-2">
                  <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                  Bank Details
                </h2>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Name <span class="text-red-500">*</span></label>
                    <input v-model="form.bank.bankName" type="text" class="input-base">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Branch Name <span class="text-red-500">*</span></label>
                    <input v-model="form.bank.branchName" type="text" class="input-base">
                  </div>           
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Number <span class="text-red-500">*</span></label>
                    <input v-model="form.bank.accountNumber" type="text" class="input-base">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Holder Name <span class="text-red-500">*</span></label>
                    <input v-model="form.bank.accountName" type="text" class="input-base">
                  </div>
                </div>

                <h2 class="text-xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-3 flex items-center gap-2">
                  <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  Security Information
                </h2>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ghana Card Number <span class="text-red-500">*</span></label>
                    <input v-model="form.security.ghanaCardNumber" type="text" class="input-base">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ghana Card Expiry Date <span class="text-red-500">*</span></label>
                    <input v-model="form.security.ghanaCardExpiry" type="date" class="input-base">
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SSNIT Number <span class="text-red-500">*</span></label>
                  <input v-model="form.security.ssnitNumber" type="text" class="input-base">
                </div>

                <h2 class="text-xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-3 flex items-center gap-2">
                  <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Document Uploads
                </h2>
                
                <div class="space-y-6">
                  <!-- CV Upload -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Curriculum Vitae (CV) <span class="text-red-500">*</span></label>
                    <div class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg hover:border-[#035a76] dark:hover:border-[#035a76] transition-colors bg-gray-50 dark:bg-gray-800/50">
                      <div class="space-y-1 text-center">
                        <svg class="mx-auto h-10 w-10 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <div class="flex text-sm text-gray-600 dark:text-gray-400 justify-center">
                          <label class="relative cursor-pointer rounded-md font-medium text-[#035a76] dark:text-blue-400 hover:text-[#024c63] focus-within:outline-none">
                            <span>Upload a file</span>
                            <input type="file" @change="handleFileUpload('cv', $event)" accept=".pdf,.doc,.docx" class="sr-only" />
                          </label>
                          <p class="pl-1">or drag and drop</p>
                        </div>
                        <p class="text-xs text-gray-500 dark:text-gray-400">PDF, DOC, DOCX up to 5MB</p>
                        <p v-if="existingDocuments.cv" class="mt-2 text-xs text-[#76ba51] font-medium flex items-center justify-center gap-1">
                          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                          Already on file: {{ existingDocuments.cv.fileName }}
                        </p>
                        <p v-if="errors.cv" class="error-msg mt-1">{{ errors.cv }}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Ghana Card Front -->
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ghana Card (Front) <span class="text-red-500">*</span></label>
                      <div class="mt-1 flex justify-center px-4 pt-4 pb-4 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg hover:border-[#035a76] dark:hover:border-[#035a76] transition-colors bg-gray-50 dark:bg-gray-800/50">
                        <div class="space-y-1 text-center w-full">
                          <svg class="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          <label class="relative cursor-pointer rounded-md font-medium text-[#035a76] dark:text-blue-400 hover:text-[#024c63] text-sm">
                            <span>Upload Image/PDF</span>
                            <input type="file" @change="handleFileUpload('ghanaCardFront', $event)" accept="image/*,.pdf" class="sr-only" />
                          </label>
                          <p v-if="existingDocuments.ghanaCardFront" class="text-xs text-[#76ba51] font-medium">✓ On file: {{ existingDocuments.ghanaCardFront.fileName }}</p>
                          <p v-if="errors.ghanaCardFront" class="error-msg">{{ errors.ghanaCardFront }}</p>
                        </div>
                      </div>
                    </div>
                    <!-- Ghana Card Back -->
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ghana Card (Back) <span class="text-red-500">*</span></label>
                      <div class="mt-1 flex justify-center px-4 pt-4 pb-4 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg hover:border-[#035a76] dark:hover:border-[#035a76] transition-colors bg-gray-50 dark:bg-gray-800/50">
                        <div class="space-y-1 text-center w-full">
                          <svg class="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          <label class="relative cursor-pointer rounded-md font-medium text-[#035a76] dark:text-blue-400 hover:text-[#024c63] text-sm">
                            <span>Upload Image/PDF</span>
                            <input type="file" @change="handleFileUpload('ghanaCardBack', $event)" accept="image/*,.pdf" class="sr-only" />
                          </label>
                          <p v-if="existingDocuments.ghanaCardBack" class="text-xs text-[#76ba51] font-medium">✓ On file: {{ existingDocuments.ghanaCardBack.fileName }}</p>
                          <p v-if="errors.ghanaCardBack" class="error-msg">{{ errors.ghanaCardBack }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- NHIS Card -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">NHIS Card <span class="text-red-500">*</span></label>
                    <div class="mt-1 flex justify-center px-4 pt-4 pb-4 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg hover:border-[#035a76] dark:hover:border-[#035a76] transition-colors bg-gray-50 dark:bg-gray-800/50">
                      <div class="space-y-1 text-center w-full">
                        <svg class="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        <label class="relative cursor-pointer rounded-md font-medium text-[#035a76] dark:text-blue-400 hover:text-[#024c63] text-sm">
                          <span>Upload Image/PDF</span>
                          <input type="file" @change="handleFileUpload('nhisCard', $event)" accept="image/*,.pdf" class="sr-only" />
                        </label>
                        <p v-if="existingDocuments.nhisCard" class="text-xs text-[#76ba51] font-medium">✓ On file: {{ existingDocuments.nhisCard.fileName }}</p>
                        <p v-if="errors.nhisCard" class="error-msg">{{ errors.nhisCard }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 6: Signature -->
              <div v-show="currentStep === 6" class="space-y-6 animate-fade-in">
                <h2 class="text-xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-3 flex items-center gap-2">
                  <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  Employee Signature
                </h2>

                <div class="border-2 border-gray-300 dark:border-gray-600 rounded-xl p-4 bg-gray-50 dark:bg-gray-800/50">
                  <canvas ref="signatureCanvas" class="w-full h-48 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 cursor-crosshair shadow-inner"></canvas>
                  <div class="flex justify-end mt-4">
                    <button type="button" @click="clearSignature" class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm">
                      <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      Clear Signature
                    </button>
                  </div>
                </div>
              </div>

              <!-- Navigation Buttons -->
              <div class="flex flex-col sm:flex-row justify-between items-center mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 gap-4">
                <button v-if="currentStep > 1" type="button" @click="prevStep" class="inline-flex items-center px-5 py-2.5 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76] dark:focus:ring-offset-gray-900 transition-colors">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                  Previous
                </button>
                <div v-else></div>

                <div class="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                  <span v-if="stepMessages[currentStep]" :class="stepMessages[currentStep].type === 'error' ? 'text-red-500' : 'text-[#76ba51]'" class="text-sm font-medium text-center sm:text-right">
                    {{ stepMessages[currentStep].text }}
                  </span>

                  <button v-if="currentStep < steps.length" type="button" @click="saveStep(currentStep)" :disabled="savingStep === currentStep" class="inline-flex items-center justify-center px-5 py-2.5 border border-[#035a76] shadow-sm text-sm font-medium rounded-lg text-[#035a76] bg-white hover:bg-[#035a76]/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <svg v-if="savingStep === currentStep" class="animate-spin -ml-1 mr-2 h-4 w-4 text-[#035a76]" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    {{ savingStep === currentStep ? 'Saving...' : (employeeId ? 'Update Draft' : 'Save Draft') }}
                  </button>

                  <button v-if="currentStep < steps.length" type="button" @click="nextStep" class="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#76ba51] hover:bg-[#65a348] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#76ba51] dark:focus:ring-offset-gray-900 transition-colors">
                    Next Step
                    <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                  </button>

                  <button v-else type="submit" :disabled="isSubmitting" class="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#76ba51] hover:bg-[#65a348] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#76ba51] dark:focus:ring-offset-gray-900 transition-colors">
                    <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    {{ isSubmitting ? 'Submitting...' : 'Submit Onboarding' }}
                  </button>
                </div>
              </div>

            </form>
          </div>
        </div>
      </main>

      <!-- Success Modal -->
      <Teleport to="body">
        <div v-if="showSuccessModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4" @click.self="showSuccessModal = false">
          <div class="bg-white dark:bg-gray-800 p-8 rounded-2xl text-center max-w-sm w-full shadow-2xl border border-gray-100 dark:border-gray-700 transform transition-all">
            <div class="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Submitted Successfully!</h3>
            <p class="text-gray-600 dark:text-gray-400 mb-6">Your onboarding form has been submitted and is under review.</p>
            <button @click="showSuccessModal = false" class="w-full px-5 py-2.5 bg-[#76ba51] text-white font-medium rounded-lg hover:bg-[#65a348] transition-colors shadow-sm">
              Close
            </button>
          </div>
        </div>
      </Teleport>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue';
import api from '@/services/api';
import HrSidebar from '@/components/hr/HrSidebar.vue';
import AcdSidebar from '@/components/acd/AcdSidebar.vue';
import CommSidebar from '@/components/com/ComSidebar.vue';
import HrTopbar from '@/components/hr/HrTopbar.vue';
import AcdTopbar from '@/components/acd/AcdTopbar.vue';
import CommTopbar from '@/components/com/ComTopbar.vue';
import ItSidebar from '@/components/it/ItSidebar.vue';
import ItTopbar from '@/components/it/ItTopbar.vue';
import SignaturePad from "signature_pad";
import { useRoute } from 'vue-router';

const route = useRoute();

const layoutMap = {
  hr: { sidebar: HrSidebar, topbar: HrTopbar },
  academics: { sidebar: AcdSidebar, topbar: AcdTopbar },
  it: { sidebar: ItSidebar, topbar: ItTopbar },
  com: { sidebar: CommSidebar, topbar: CommTopbar },
};

const currentModule = computed(() => {
  if (route.path.startsWith('/academics')) return 'academics';
  if (route.path.startsWith('/it')) return 'it';
  if (route.path.startsWith('/com')) return 'com';
  if (route.path.startsWith('/procurement')) return 'procurement';
  return 'hr';
});

const signatureCanvas = ref(null);
let signaturePad = null;
const signatureData = ref(null);
const currentPageTitle = ref('Onboarding');
const isSidebarOpen = ref(false); // Hidden by default on mobile
const isDarkMode = ref(false);

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.theme = 'light';
  }
};

const steps = ['Personal Info', 'Family Info', 'Work Experience', 'Education', 'Contacts & Docs', 'Signature'];
const currentStep = ref(1);
const maritalStatuses = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' }
];

const form = ref({
  personal: { firstName: '', middleName: '', lastName: '', gender: '', dob: '', placeOfBirth: '', nationality: '', address: '', phone: '', email: '' },
  marital: { status: '', spouseName: '', hasChildren: false, numberOfChildren: 1, children: [{ fullName: '', age: '' }] },
  parents: { fatherName: '', fatherContact: '', motherName: '', motherContact: '', address: '' },
  workExperience: [{ company: '', position: '', address: '', contact: '', startDate: '', endDate: '', reasonForLeaving: '' }],
  education: [{ certificate: '', otherCertificate: '', school: '', startYear: '', endYear: '' }],
  emergencyContact: { fullName: '', relationship: '', phone: '', altPhone: '', address: '' },
  bank: { bankName: '', branchName: '', accountNumber: '', accountName: '' },
  security: { ghanaCardNumber: '', ghanaCardExpiry: '', ssnitNumber: '' },
  documents: { cv: null, ghanaCardFront: null, ghanaCardBack: null, nhisCard: null }
});

const errors = ref({
  firstName: '', lastName: '', gender: '', dob: '', placeOfBirth: '', nationality: '', address: '', phone: '', email: '',
  status: '', spouseName: '', fatherName: '', fatherContact: '', motherName: '', motherContact: '', parentsAddress: '',
  emergencyFullName: '', emergencyRelationship: '', emergencyPhone: '', emergencyAltPhone: '', emergencyAddress: '',
  bankName: '', branchName: '', accountNumber: '', accountName: '',
  ghanaCardNumber: '', ghanaCardExpiry: '', ssnitNumber: '',
  cv: '', ghanaCardFront: '', ghanaCardBack: '', nhisCard: ''
});

const clearErrors = () => { Object.keys(errors.value).forEach(key => { errors.value[key] = ''; }); };
const isEmpty = (value) => value === null || value === undefined || String(value).trim() === '';
const isValidName = (value) => /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/.test(value.trim());
const isValidPhone = (value) => /^\d{10}$/.test(String(value).trim());
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const isValidDate = (value) => { if (!value) return false; return !isNaN(new Date(value).getTime()); };
const isFutureDate = (value) => { if (!value) return false; const d = new Date(value); const t = new Date(); t.setHours(0,0,0,0); d.setHours(0,0,0,0); return d > t; };
const isValidYear = (value) => { const y = Number(value); return Number.isInteger(y) && y >= 1900 && y <= new Date().getFullYear(); };
const isValidAccountNumber = (value) => /^\d+$/.test(String(value).trim());

const validateCurrentStep = () => {
  clearErrors();
  let valid = true;

  if (currentStep.value === 1) {
    if (isEmpty(form.value.personal.firstName)) { errors.value.firstName = 'First name is required.'; valid = false; }
    else if (!isValidName(form.value.personal.firstName)) { errors.value.firstName = 'First name can only contain letters, spaces, hyphens or apostrophes.'; valid = false; }
    if (isEmpty(form.value.personal.lastName)) { errors.value.lastName = 'Last name is required.'; valid = false; }
    else if (!isValidName(form.value.personal.lastName)) { errors.value.lastName = 'Last name can only contain letters, spaces, hyphens or apostrophes.'; valid = false; }
    if (isEmpty(form.value.personal.gender)) { errors.value.gender = 'Please select a gender.'; valid = false; }
    if (isEmpty(form.value.personal.dob)) { errors.value.dob = 'Date of birth is required.'; valid = false; }
    else if (!isValidDate(form.value.personal.dob)) { errors.value.dob = 'Please enter a valid date of birth.'; valid = false; }
    else if (isFutureDate(form.value.personal.dob)) { errors.value.dob = 'Date of birth cannot be in the future.'; valid = false; }
    if (isEmpty(form.value.personal.placeOfBirth)) { errors.value.placeOfBirth = 'Place of birth is required.'; valid = false; }
    if (isEmpty(form.value.personal.nationality)) { errors.value.nationality = 'Nationality is required.'; valid = false; }
    if (isEmpty(form.value.personal.address)) { errors.value.address = 'Residential address is required.'; valid = false; }
    if (isEmpty(form.value.personal.phone)) { errors.value.phone = 'Phone number is required.'; valid = false; }
    else if (!isValidPhone(form.value.personal.phone)) { errors.value.phone = 'Phone number must contain exactly 10 digits.'; valid = false; }
    if (isEmpty(form.value.personal.email)) { errors.value.email = 'Email address is required.'; valid = false; }
    else if (!isValidEmail(form.value.personal.email)) { errors.value.email = 'Please enter a valid email address.'; valid = false; }
  }

  if (currentStep.value === 2) {
    if (isEmpty(form.value.marital.status)) { errors.value.status = 'Please select your marital status.'; valid = false; }
    if (form.value.marital.status === 'married') {
      if (isEmpty(form.value.marital.spouseName)) { errors.value.spouseName = 'Spouse name is required.'; valid = false; }
      else if (!isValidName(form.value.marital.spouseName)) { errors.value.spouseName = 'Please enter a valid spouse name.'; valid = false; }
    }
    if (form.value.marital.hasChildren) {
      const n = Number(form.value.marital.numberOfChildren);
      if (!Number.isInteger(n) || n < 1 || n > 20) { alert('Number of children must be between 1 and 20.'); valid = false; }
      form.value.marital.children.forEach((child, index) => {
        if (isEmpty(child.fullName)) { alert(`Child ${index + 1}: Full name is required.`); valid = false; }
        else if (!isValidName(child.fullName)) { alert(`Child ${index + 1}: Please enter a valid name.`); valid = false; }
        const age = Number(child.age);
        if (child.age === '' || !Number.isInteger(age) || age < 0 || age > 30) { alert(`Child ${index + 1}: Age must be between 0 and 30.`); valid = false; }
      });
    }
    if (isEmpty(form.value.parents.fatherName)) { errors.value.fatherName = "Father's name is required."; valid = false; }
    else if (!isValidName(form.value.parents.fatherName)) { errors.value.fatherName = "Please enter a valid father's name."; valid = false; }
    if (isEmpty(form.value.parents.fatherContact)) { errors.value.fatherContact = "Father's contact is required."; valid = false; }
    else if (!isValidPhone(form.value.parents.fatherContact)) { errors.value.fatherContact = "Father's contact must contain exactly 10 digits."; valid = false; }
    if (isEmpty(form.value.parents.motherName)) { errors.value.motherName = "Mother's name is required."; valid = false; }
    else if (!isValidName(form.value.parents.motherName)) { errors.value.motherName = "Please enter a valid mother's name."; valid = false; }
    if (!isEmpty(form.value.parents.motherContact) && !isValidPhone(form.value.parents.motherContact)) { errors.value.motherContact = "Mother's contact must contain exactly 10 digits."; valid = false; }
    if (isEmpty(form.value.parents.address)) { errors.value.parentsAddress = "Parents' address is required."; valid = false; }
  }

  if (currentStep.value === 3) {
    form.value.workExperience.forEach((exp, index) => {
      if (isValidDate(exp.startDate) && isValidDate(exp.endDate) && new Date(exp.endDate) < new Date(exp.startDate)) {
        alert(`Previous Work ${index + 1}: End date cannot be before start date.`); valid = false;
      }
    });
  }

  if (currentStep.value === 4) {
    form.value.education.forEach((edu, index) => {
      if (isEmpty(edu.certificate)) { alert(`Education ${index + 1}: Certificate/Degree is required.`); valid = false; }
      if (edu.certificate === 'Other' && isEmpty(edu.otherCertificate)) { alert(`Education ${index + 1}: Please specify the certificate.`); valid = false; }
      if (isEmpty(edu.school)) { alert(`Education ${index + 1}: School name is required.`); valid = false; }
      if (!isValidYear(edu.startYear)) { alert(`Education ${index + 1}: Start year must be between 1900 and ${new Date().getFullYear()}.`); valid = false; }
      if (!isValidYear(edu.endYear)) { alert(`Education ${index + 1}: End year must be between 1900 and ${new Date().getFullYear()}.`); valid = false; }
      if (isValidYear(edu.startYear) && isValidYear(edu.endYear) && Number(edu.endYear) < Number(edu.startYear)) {
        alert(`Education ${index + 1}: End year cannot be before start year.`); valid = false;
      }
    });
  }

  if (currentStep.value === 5) {
    if (isEmpty(form.value.emergencyContact.fullName)) { errors.value.emergencyFullName = 'Emergency contact name is required.'; valid = false; }
    else if (!isValidName(form.value.emergencyContact.fullName)) { errors.value.emergencyFullName = 'Please enter a valid emergency contact name.'; valid = false; }
    if (isEmpty(form.value.emergencyContact.relationship)) { errors.value.emergencyRelationship = 'Relationship is required.'; valid = false; }
    if (isEmpty(form.value.emergencyContact.phone)) { errors.value.emergencyPhone = 'Emergency phone number is required.'; valid = false; }
    else if (!isValidPhone(form.value.emergencyContact.phone)) { errors.value.emergencyPhone = 'Emergency phone must contain exactly 10 digits.'; valid = false; }
  }

  return valid;
};

const nextStep = () => { if (!validateCurrentStep()) return; if (currentStep.value < steps.length) currentStep.value++; };
const prevStep = () => { if (currentStep.value > 1) currentStep.value--; };

const updateChildrenFields = () => {
  const currentCount = form.value.marital.children.length;
  const newCount = form.value.marital.numberOfChildren;
  if (newCount > currentCount) { for (let i = currentCount; i < newCount; i++) form.value.marital.children.push({ fullName: '', age: '' }); }
  else if (newCount < currentCount) { form.value.marital.children.splice(newCount, currentCount - newCount); }
};
const addChild = () => { form.value.marital.children.push({ fullName: '', age: '' }); form.value.marital.numberOfChildren = form.value.marital.children.length; };
const removeChild = (index) => { form.value.marital.children.splice(index, 1); form.value.marital.numberOfChildren = form.value.marital.children.length; };
const addWorkExperience = () => { form.value.workExperience.push({ company: '', position: '', address: '', contact: '', startDate: '', endDate: '', reasonForLeaving: '' }); };
const removeWorkExperience = (index) => { form.value.workExperience.splice(index, 1); };
const addEducation = () => { form.value.education.push({ certificate: '', otherCertificate: '', school: '', startYear: '', endYear: '' }); };
const removeEducation = (index) => { form.value.education.splice(index, 1); };

const handleFileUpload = (field, event) => {
  const file = event.target.files?.[0];
  if (!file) { form.value.documents[field] = null; return; }
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  if (file.size > MAX_FILE_SIZE) { errors.value[field] = 'File size must not exceed 5 MB.'; form.value.documents[field] = null; event.target.value = ''; return; }
  if (field === 'cv') {
    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) { errors.value.cv = 'CV must be a PDF, DOC, or DOCX file.'; form.value.documents[field] = null; event.target.value = ''; return; }
  }
  if (['ghanaCardFront', 'ghanaCardBack', 'nhisCard'].includes(field)) {
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { errors.value[field] = 'File must be an image or PDF.'; form.value.documents[field] = null; event.target.value = ''; return; }
  }
  errors.value[field] = '';
  form.value.documents[field] = file;
};

const createFormData = (formObj) => {
  const formData = new FormData();
  Object.keys(formObj.personal).forEach(key => formData.append(`personal[${key}]`, formObj.personal[key]));
  Object.keys(formObj.marital).forEach(key => {
    if (key === 'children') { formObj.marital.children.forEach((child, i) => { Object.keys(child).forEach(cKey => formData.append(`marital[children][${i}][${cKey}]`, child[cKey])); }); }
    else { formData.append(`marital[${key}]`, formObj.marital[key]); }
  });
  Object.keys(formObj.parents).forEach(key => formData.append(`parents[${key}]`, formObj.parents[key]));
  formObj.workExperience.forEach((exp, i) => { Object.keys(exp).forEach(key => formData.append(`workExperience[${i}][${key}]`, exp[key])); });
  formObj.education.forEach((edu, i) => { Object.keys(edu).forEach(key => formData.append(`education[${i}][${key}]`, edu[key])); });
  Object.keys(formObj.emergencyContact).forEach(key => formData.append(`emergencyContact[${key}]`, formObj.emergencyContact[key]));
  Object.keys(formObj.bank).forEach(key => formData.append(`bank[${key}]`, formObj.bank[key]));
  Object.keys(formObj.security).forEach(key => formData.append(`security[${key}]`, formObj.security[key]));
  Object.keys(formObj.documents).forEach(key => { if (formObj.documents[key]) formData.append(`documents[${key}]`, formObj.documents[key]); });
  return formData;
};

const employeeId = ref(null);
const isLoadingDraft = ref(false);
const savingStep = ref(null);
const stepMessages = ref({});
const existingDocuments = ref({ cv: null, ghanaCardFront: null, ghanaCardBack: null, nhisCard: null });
const stepKeyMap = { 1: 'personal', 2: 'family', 3: 'work', 4: 'education', 5: 'contacts' };

const buildStepFormData = (stepKey) => {
  const formData = new FormData();
  formData.append('action', 'save_step');
  formData.append('step', stepKey);
  if (stepKey === 'personal') {
    Object.keys(form.value.personal).forEach(key => formData.append(`personal[${key}]`, form.value.personal[key] ?? ''));
    formData.append('marital[status]', form.value.marital.status ?? '');
  }
  if (stepKey === 'family') {
    Object.keys(form.value.marital).forEach(key => {
      if (key === 'children') { form.value.marital.children.forEach((child, i) => { Object.keys(child).forEach(cKey => formData.append(`marital[children][${i}][${cKey}]`, child[cKey])); }); }
      else { formData.append(`marital[${key}]`, form.value.marital[key] ?? ''); }
    });
    Object.keys(form.value.parents).forEach(key => formData.append(`parents[${key}]`, form.value.parents[key] ?? ''));
  }
  if (stepKey === 'work') { form.value.workExperience.forEach((exp, i) => { Object.keys(exp).forEach(key => formData.append(`workExperience[${i}][${key}]`, exp[key] ?? '')); }); }
  if (stepKey === 'education') { form.value.education.forEach((edu, i) => { Object.keys(edu).forEach(key => formData.append(`education[${i}][${key}]`, edu[key] ?? '')); }); }
  if (stepKey === 'contacts') {
    Object.keys(form.value.emergencyContact).forEach(key => formData.append(`emergencyContact[${key}]`, form.value.emergencyContact[key] ?? ''));
    Object.keys(form.value.bank).forEach(key => formData.append(`bank[${key}]`, form.value.bank[key] ?? ''));
    Object.keys(form.value.security).forEach(key => formData.append(`security[${key}]`, form.value.security[key] ?? ''));
    Object.keys(form.value.documents).forEach(key => { if (form.value.documents[key]) formData.append(`documents[${key}]`, form.value.documents[key]); });
  }
  return formData;
};

const saveStep = async (stepNumber) => {
  const stepKey = stepKeyMap[stepNumber];
  if (!stepKey) return;
  savingStep.value = stepNumber;
  stepMessages.value = { ...stepMessages.value, [stepNumber]: null };
  try {
    const formData = buildStepFormData(stepKey);
    const response = await api.post('Staff.php', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (response.data?.success === false) {
      stepMessages.value = { ...stepMessages.value, [stepNumber]: { type: 'error', text: response.data.message || 'Could not save this step.' } };
      return;
    }
    if (response.data?.employeeId) employeeId.value = response.data.employeeId;
    if (stepKey === 'contacts' && response.data?.documents) existingDocuments.value = { ...existingDocuments.value, ...response.data.documents };
    stepMessages.value = { ...stepMessages.value, [stepNumber]: { type: 'success', text: 'Saved successfully.' } };
  } catch (error) {
    console.error('Save step failed:', error);
    stepMessages.value = { ...stepMessages.value, [stepNumber]: { type: 'error', text: error.response?.data?.message || 'Failed to save.' } };
  } finally { savingStep.value = null; }
};

const loadDraft = async () => {
  isLoadingDraft.value = true;
  try {
    const response = await api.get('Staff.php', { params: { action: 'load_draft' } });
    const data = response.data;
    if (!data?.success || !data.employeeId) return;
    employeeId.value = data.employeeId;
    if (data.personal) Object.assign(form.value.personal, data.personal);
    if (data.marital) {
      const { children, ...maritalRest } = data.marital;
      Object.assign(form.value.marital, maritalRest);
      if (Array.isArray(children) && children.length > 0) {
        form.value.marital.children = children;
        form.value.marital.hasChildren = true;
        form.value.marital.numberOfChildren = children.length;
      }
    }
    if (data.parents) Object.assign(form.value.parents, data.parents);
    if (Array.isArray(data.workExperience) && data.workExperience.length > 0) form.value.workExperience = data.workExperience;
    if (Array.isArray(data.education) && data.education.length > 0) form.value.education = data.education;
    if (data.emergencyContact) Object.assign(form.value.emergencyContact, data.emergencyContact);
    if (data.bank) Object.assign(form.value.bank, data.bank);
    if (data.security) Object.assign(form.value.security, data.security);
    if (data.documents) existingDocuments.value = { ...existingDocuments.value, ...data.documents };
  } catch (error) { console.warn('No draft loaded:', error); }
  finally { isLoadingDraft.value = false; }
};

const isSubmitting = ref(false);
const showSuccessModal = ref(false);

const clearSignature = () => { if (signaturePad) signaturePad.clear(); };

const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
};

const submitForm = async () => {
  if (isSubmitting.value) return;
  const validationResult = validateAllSteps();
  if (!validationResult) { alert('Please correct the validation errors before submitting.'); return; }
  if (!signaturePad || signaturePad.isEmpty()) { alert("Please sign before submitting"); return; }

  isSubmitting.value = true;
  try {
    const formData = createFormData(form.value);
    formData.append('action', 'submit');
    const signatureDataURL = signaturePad.toDataURL();
    const signatureBlob = dataURLtoBlob(signatureDataURL);
    formData.append("signature", signatureBlob, "signature.png");

    const response = await api.post("Staff.php", formData, { headers: { "Content-Type": "multipart/form-data" } });
    if (response.data?.success === false || response.data?.status === "error") {
      alert(response.data.message || "Submission failed. Please check your details and try again.");
      return;
    }
    resetForm();
    showSuccessModal.value = true;
  } catch (error) {
    console.error("Submit failed:", error);
    if (error.response) alert(error.response.data?.message || `Server error (${error.response.status}).`);
    else if (error.request) alert("No response from server. Check your connection.");
    else alert("Failed to submit. Check console or try again.");
  } finally { isSubmitting.value = false; }
};

const validateAllSteps = () => {
  const originalStep = currentStep.value;
  for (let step = 1; step <= 5; step++) {
    currentStep.value = step;
    if (!validateCurrentStep()) { currentStep.value = originalStep; return false; }
  }
  currentStep.value = originalStep;
  return true;
};

const resetForm = () => {
  form.value = {
    personal: { firstName: '', middleName: '', lastName: '', gender: '', dob: '', placeOfBirth: '', nationality: '', address: '', phone: '', email: '' },
    marital: { status: '', spouseName: '', hasChildren: false, numberOfChildren: 1, children: [{ fullName: '', age: '' }] },
    parents: { fatherName: '', fatherContact: '', motherName: '', motherContact: '', address: '' },
    workExperience: [{ company: '', position: '', address: '', contact: '', startDate: '', endDate: '', reasonForLeaving: '' }],
    education: [{ certificate: '', otherCertificate: '', school: '', startYear: '', endYear: '' }],
    emergencyContact: { fullName: '', relationship: '', phone: '', altPhone: '', address: '' },
    bank: { bankName: '', branchName: '', accountNumber: '', accountName: '' },
    security: { ghanaCardNumber: '', ghanaCardExpiry: '', ssnitNumber: '' },
    documents: { cv: null, ghanaCardFront: null, ghanaCardBack: null, nhisCard: null }
  };
  currentStep.value = 1;
  employeeId.value = null;
  stepMessages.value = {};
  existingDocuments.value = { cv: null, ghanaCardFront: null, ghanaCardBack: null, nhisCard: null };
};

onMounted(() => {
  loadDraft();
  nextTick(() => {
    if (signatureCanvas.value) signaturePad = new SignaturePad(signatureCanvas.value);
  });
});
</script>

<style scoped>
.input-base {
  @apply mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-[#035a76] focus:border-transparent transition-colors;
}
.error-msg {
  @apply text-red-500 text-xs mt-1 flex items-center gap-1;
}
.error-msg::before {
  content: "⚠";
  font-size: 0.8rem;
}
.animate-fade-in {
  animation: fadeIn 0.3s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.fade-enter-active, .fade-leave-active { transition: all 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-10px); }
</style>